import { Box, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PassiveParameterTypeEnum } from '../../../model/passive/enum/passiveParameterTypeEnum';
import { ParameterCard } from '../../documented/ParameterCard';
import { RadialIndicator } from '../../documented/RadialIndicator';

import getRadialConfig from '../../documented/RadialIndicator/radialConfig';
import { ComplianceOverview, CompliancePeriod } from './ComplianceCardData';
interface IComplianceCardProps {
  onClick?: () => void;
  title: string;
  popoverContent: React.ReactNode;
  data?: ComplianceOverview;
  large?: boolean;
}

const ParamContentContainer = styled(Box)(({ theme }) => ({
  ...theme.typography.body2,
  textAlign: 'center',
}));

const ComplianceCard: React.FC<IComplianceCardProps> = ({
  onClick = () => {},
  title,
  popoverContent,
  data,
  large,
}: IComplianceCardProps) => {
  const { t } = useTranslation('cards');
  const [previousPeriodData, setPreviousPeriodData] =
    useState<CompliancePeriod | null>(null);

  const [currentPeriodData, setCurrentPeriodData] =
    useState<CompliancePeriod | null>(null);

  useEffect(() => {
    if (data) {
      setPreviousPeriodData(data.previousPeriod);
      setCurrentPeriodData(data.currentPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <ParameterCard
      large={large}
      title={title}
      popoverContent={popoverContent}
      onClick={onClick}
    >
      <Grid container sx={{ flexDirection: 'column' }}>
        <Grid>
          <Typography sx={{ fontSize: 14,  textAlign: 'left', ml: 4 }}>
            <strong>
              {t('compliance.asOf', {
                date: new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }),
              })}
            </strong>
          </Typography>
        </Grid>

        <Grid container sx={{ justifyContent: 'center' }}>
          <Grid size={6} container sx={{ alignItems: 'center',  flexDirection: 'column' }}>
            <ParamContentContainer>
              <RadialIndicator
                {...getRadialConfig(
                  PassiveParameterTypeEnum.Compliance,
                  previousPeriodData ? previousPeriodData.daysTotal : 0
                )}
                size={200}
                value={previousPeriodData ? previousPeriodData.daysOfUse : 0}
                unit={t('compliance.daysOf', {
                  total: previousPeriodData ? previousPeriodData.daysTotal : 0,
                })}
                disabled={
                  !previousPeriodData?.daysOfUse &&
                  !previousPeriodData?.daysTotal
                }
              />
              {previousPeriodData?.endTime && (
                <Typography sx={{ textAlign: 'center' }}>
                  {t('compliance.previousPeriod')} <br />{' '}
                  {t('compliance.endedOn')} <br />
                  {`${new Date(previousPeriodData.endTime).toLocaleDateString(
                    'en-US',
                    {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    }
                  )}`}
                </Typography>
              )}
            </ParamContentContainer>
          </Grid>

          <Grid size={6} container sx={{ alignItems: 'center',  flexDirection: 'column' }}>
            <ParamContentContainer>
              <RadialIndicator
                {...getRadialConfig(
                  PassiveParameterTypeEnum.Compliance,
                  currentPeriodData ? currentPeriodData.daysTotal : undefined
                )}
                size={200}
                value={
                  currentPeriodData ? currentPeriodData.daysOfUse : undefined
                }
                unit={t('compliance.daysOf', {
                  total: currentPeriodData
                    ? currentPeriodData.daysTotal
                    : undefined,
                })}
                disabled={
                  !currentPeriodData?.daysOfUse && !currentPeriodData?.daysTotal
                }
              />
              {currentPeriodData?.endTime && (
                <Typography sx={{ textAlign: 'center' }}>
                  {t('compliance.currentPeriod')} <br />{' '}
                  {t('compliance.endingOn')} <br />
                  {`${new Date(currentPeriodData.endTime).toLocaleDateString(
                    'en-US',
                    {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    }
                  )}`}
                </Typography>
              )}
            </ParamContentContainer>
          </Grid>
        </Grid>
      </Grid>
    </ParameterCard>
  );
};

export default ComplianceCard;
